import { throwErrorMessage } from 'dashboard/store/utils/api';
import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import * as types from '../mutation-types';
import CannedResponseAPI from '../../api/cannedResponse';

const state = {
  records: [],
  uiFlags: {
    fetchingList: false,
    fetchingItem: false,
    creatingItem: false,
    updatingItem: false,
    deletingItem: false,
  },
  detectedLanguage: null,
};

const getters = {
  getCannedResponses(_state) {
    return _state.records;
  },
  getDetectedLanguage(_state) {
    return _state.detectedLanguage;
  },
  getLocalizedCannedResponses(_state) {
    return locale => {
      if (!locale) {
        return _state.records;
      }

      const normalized = locale.toLowerCase();

      return _state.records.filter(record => {
        if (!record.locale) {
          return false;
        }

        return record.locale.toLowerCase() === normalized;
      });
    };
  },
  getSortedCannedResponses(_state) {
    return sortOrder =>
      [..._state.records].sort((a, b) => {
        if (sortOrder === 'asc') {
          return a.short_code.localeCompare(b.short_code);
        }
        return b.short_code.localeCompare(a.short_code);
      });
  },
  getUIFlags(_state) {
    return _state.uiFlags;
  },
};

const actions = {
  getCannedResponse: async function getCannedResponse(
    { commit },
    { searchKey } = {}
  ) {
    commit(types.default.SET_CANNED_UI_FLAG, { fetchingList: true });
    try {
      const response = await CannedResponseAPI.get({ searchKey });
      commit(types.default.SET_CANNED, response.data);
      commit(types.default.SET_CANNED_UI_FLAG, { fetchingList: false });
    } catch (error) {
      commit(types.default.SET_CANNED_UI_FLAG, { fetchingList: false });
    }
  },

  setDetectedCannedLanguage: ({ commit }, locale) => {
    console.info('[store/cannedResponse] setDetectedCannedLanguage action', {
      locale,
    });
    commit(types.default.SET_DETECTED_CANNED_LANGUAGE, locale);
  },

  createCannedResponse: async function createCannedResponse(
    { commit },
    cannedObj
  ) {
    commit(types.default.SET_CANNED_UI_FLAG, { creatingItem: true });
    try {
      const response = await CannedResponseAPI.create(cannedObj);
      commit(types.default.ADD_CANNED, response.data);
      commit(types.default.SET_CANNED_UI_FLAG, { creatingItem: false });
      return response.data;
    } catch (error) {
      commit(types.default.SET_CANNED_UI_FLAG, { creatingItem: false });
      return throwErrorMessage(error);
    }
  },

  updateCannedResponse: async function updateCannedResponse(
    { commit },
    { id, ...updateObj }
  ) {
    commit(types.default.SET_CANNED_UI_FLAG, { updatingItem: true });
    try {
      const response = await CannedResponseAPI.update(id, updateObj);
      commit(types.default.EDIT_CANNED, response.data);
      commit(types.default.SET_CANNED_UI_FLAG, { updatingItem: false });
      return response.data;
    } catch (error) {
      commit(types.default.SET_CANNED_UI_FLAG, { updatingItem: false });
      return throwErrorMessage(error);
    }
  },

  deleteCannedResponse: async function deleteCannedResponse({ commit }, id) {
    commit(types.default.SET_CANNED_UI_FLAG, { deletingItem: true });
    try {
      await CannedResponseAPI.delete(id);
      commit(types.default.DELETE_CANNED, id);
      commit(types.default.SET_CANNED_UI_FLAG, { deletingItem: true });
      return id;
    } catch (error) {
      commit(types.default.SET_CANNED_UI_FLAG, { deletingItem: true });
      return throwErrorMessage(error);
    }
  },

  createCannedResponseTranslation: async function createCannedResponseTranslation(
    { commit },
    { cannedResponseId, locale, content }
  ) {
    commit(types.default.SET_CANNED_UI_FLAG, { creatingItem: true });
    try {
      const response = await CannedResponseAPI.createTranslation(
        cannedResponseId,
        { locale, content }
      );
      commit(types.default.SET_CANNED_UI_FLAG, { creatingItem: false });
      return response.data;
    } catch (error) {
      commit(types.default.SET_CANNED_UI_FLAG, { creatingItem: false });
      return throwErrorMessage(error);
    }
  },

  getCannedResponseTranslations: async function getCannedResponseTranslations(
    { commit },
    cannedResponseId
  ) {
    commit(types.default.SET_CANNED_UI_FLAG, { fetchingItem: true });
    try {
      const response = await CannedResponseAPI.getTranslations(cannedResponseId);
      commit(types.default.SET_CANNED_UI_FLAG, { fetchingItem: false });
      return response.data;
    } catch (error) {
      commit(types.default.SET_CANNED_UI_FLAG, { fetchingItem: false });
      return [];
    }
  },

  updateCannedResponseTranslation: async function updateCannedResponseTranslation(
    { commit },
    { cannedResponseId, translationId, locale, content }
  ) {
    commit(types.default.SET_CANNED_UI_FLAG, { updatingItem: true });
    try {
      const response = await CannedResponseAPI.updateTranslation(
        cannedResponseId,
        translationId,
        { locale, content }
      );
      commit(types.default.SET_CANNED_UI_FLAG, { updatingItem: false });
      return response.data;
    } catch (error) {
      commit(types.default.SET_CANNED_UI_FLAG, { updatingItem: false });
      return throwErrorMessage(error);
    }
  },

  importCannedResponses: async function importCannedResponses(
    { commit, dispatch },
    file
  ) {
    commit(types.default.SET_CANNED_UI_FLAG, { creatingItem: true });
    try {
      const response = await CannedResponseAPI.importCannedResponses(file);
      const results = response.data || {};

      if (results.success > 0) {
        await dispatch('getCannedResponse');
      }

      return results;
    } catch (error) {
      const responseData = error?.response?.data;
      if (responseData && typeof responseData === 'object') {
        return responseData;
      }
      return throwErrorMessage(error);
    } finally {
      commit(types.default.SET_CANNED_UI_FLAG, { creatingItem: false });
    }
  },

  exportCannedResponses: async function exportCannedResponses({ commit }) {
    commit(types.default.SET_CANNED_UI_FLAG, { fetchingList: true });
    try {
      const response = await CannedResponseAPI.exportCannedResponses();

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `canned_responses_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      return { success: true };
    } catch (error) {
      return throwErrorMessage(error);
    } finally {
      commit(types.default.SET_CANNED_UI_FLAG, { fetchingList: false });
    }
  },

  deleteAllCannedResponses: async function deleteAllCannedResponses({ commit }) {
    commit(types.default.SET_CANNED_UI_FLAG, { deletingItem: true });
    try {
      await CannedResponseAPI.deleteAll();
      commit(types.default.SET_CANNED, []);
      commit(types.default.SET_CANNED_UI_FLAG, { deletingItem: false });
      return { success: true };
    } catch (error) {
      commit(types.default.SET_CANNED_UI_FLAG, { deletingItem: false });
      return throwErrorMessage(error);
    }
  },
};

const mutations = {
  [types.default.SET_CANNED_UI_FLAG](_state, data) {
    _state.uiFlags = {
      ..._state.uiFlags,
      ...data,
    };
  },

  [types.default.SET_CANNED]: MutationHelpers.set,
  [types.default.ADD_CANNED]: MutationHelpers.create,
  [types.default.EDIT_CANNED]: MutationHelpers.update,
  [types.default.DELETE_CANNED]: MutationHelpers.destroy,
  [types.default.SET_DETECTED_CANNED_LANGUAGE](_state, language) {
    console.info('[store/cannedResponse] mutation SET_DETECTED_CANNED_LANGUAGE', {
      language,
    });
    _state.detectedLanguage = language;
  },
};

export default {
  state,
  getters,
  actions,
  mutations,
};
